import { api, ApiError, type BillingHistoryEntry, type Entitlements, type SubscriptionSummary } from '../api.ts';
import { el, formatDate, mount, onTap, relativeTime } from '../ui/dom.ts';
import { iconEl } from '../ui/icons.ts';
import { appMain, ensureAppShell, logout, pageIntro } from '../ui/shell.ts';
import { confirmDialog, errorNote, okNote, skeletonPlans, skeletonRows, stateBlock, toast } from '../ui/feedback.ts';
import { navigate, renderGuard } from '../router.ts';
import { loadSession, state } from '../state.ts';
import { planSection, type PlansPayload } from './plans-section.ts';
import { play } from '../playback.ts';
import { isCar } from '../ui/surface.ts';
import { t } from '../i18n/index.ts';
import { langSwitch } from '../ui/lang-switch.ts';

function wrap(...children: Array<Node | null>): HTMLElement {
  return el('div', { class: 'wrap' }, ...children);
}

// ================================= Paketler =================================
// Landing ile AYNI fiyat bileşeni; açık bir "sayfa" üzerinde duruyor.

export async function plansView(_params?: Record<string, string>, query?: URLSearchParams): Promise<void> {
  ensureAppShell();
  const host = appMain();
  const here = query?.get('checkout') === 'here';
  mount(host, wrap(pageIntro(t('plans.page.title'), null, t('plans.page.sub')), el('div', { class: 'pricing' }, skeletonPlans())));

  // iyzico'nun ödeme sayfasından dönüş: sonuç sunucuda işlendi, oturum tazelenir.
  // Parametre adresten silinir ki sayfa yenilenince bildirim yeniden çıkmasın.
  const payment = query?.get('payment');
  if (payment) {
    history.replaceState(history.state, '', '/plans');
    if (payment === 'success') { navigate('/checkout/done', true); return; }
    if (payment !== 'pending') toast(t(payment === 'expired' ? 'pay.expired' : 'pay.failed'), 'error', 7000);
  }
  // Kart güncelleme formundan dönüş.
  const card = query?.get('card');
  if (card) {
    history.replaceState(history.state, '', '/plans');
    if (card === 'done') { await loadSession(); toast(t('sub.cardDone'), 'ok', 9000); }
    // Dönüşün sonucu anlaşılamadı: "güncellendi" denmez.
    else if (card === 'unknown') toast(t('sub.cardUnknown'), 'info', 10000);
    else toast(t('sub.cardFailed'), 'error', 8000);
  }

  // Yanıt gelene dek başka ekrana geçildiyse o ekranı ezme.
  const stale = renderGuard();
  let data: PlansPayload;
  try {
    data = await api.get<PlansPayload>('/api/billing/plans');
  } catch (err) {
    if (stale()) return;
    mount(host, wrap(pageIntro(t('plans.page.title')), stateBlock({ title: t('plans.page.loadFail'), body: (err as Error).message })));
    return;
  }
  if (stale()) return;

  mount(host, wrap(
    pageIntro(t('plans.page.title'), null, t('plans.page.sub')),
    el('div', { class: 'pricing' }, planSection(data, { context: 'app', forceCheckoutHere: here, onChanged: () => void plansView() })),
    subscriptionSection(),
    historySection(stale),
    addonSection(data),
  ));

  // Ödeme alındı ama sonucu öğrenilemedi (dönüşte sağlayıcıya ulaşılamadı ya da
  // sekme ödeme sayfasında kapandı): ekran çizildikten sonra yeniden sorulur.
  if (payment === 'pending' || data.pendingCheckout) void reconcilePayment(payment === 'pending', stale);
}

async function reconcilePayment(announce: boolean, stale: () => boolean): Promise<void> {
  if (announce) toast(t('pay.verifying'), 'info', 5000);
  let outcome = 'pending';
  try {
    outcome = (await api.post<{ outcome: string }>('/api/billing/reconcile')).outcome;
  } catch { /* sonuç yine bilinmiyor */ }
  if (outcome === 'done') {
    await loadSession();
    // Geç doğrulanan ödeme de aynı onay ekranına gider.
    if (!stale()) navigate('/checkout/done', true);
    else toast(t('pay.success'), 'ok', 6000);
    return;
  }
  if (!announce) return;
  if (outcome === 'failed') toast(t('pay.failed'), 'error', 7000);
  else if (outcome === 'pending') toast(t('pay.pending'), 'info', 10000);
}

/** Aboneliğin tarihle ilgili satırı. Eskiden tek bir "Yenileme: <tarih>" vardı ve
 *  deneme bitişi, gerçek yenileme ve iptal sonrası erişim bitişi aynı görünüyordu. */
function subscriptionDateRow(ent: Entitlements, sub: SubscriptionSummary | undefined): HTMLElement {
  // Eski sunucu sürümü özeti göndermez: önceki tek satıra dönülür.
  if (!sub) return infoRow(t('sub.renewal'), ent.renewsAt ? formatDate(ent.renewsAt) : t('sub.noRenewal'));
  if (sub.nextChargeAt) {
    return infoRow(t(sub.inTrial ? 'sub.trialEnds' : 'sub.nextCharge'),
      t('sub.chargeValue', { date: formatDate(sub.nextChargeAt), price: sub.priceLabel ?? '' }));
  }
  if (sub.status === 'canceled') {
    return infoRow(t('sub.accessEnds'), sub.endsAt ? t('sub.accessEndsValue', { date: formatDate(sub.endsAt) }) : '—');
  }
  if (sub.status === 'unpaid') return infoRow(t('sub.accessEnds'), sub.endsAt ? formatDate(sub.endsAt) : '—');
  // Sağlayıcısız (elle atanmış) paket: tahsilat yok, yalnız bitiş tarihi olabilir.
  return infoRow(t('sub.planEnds'), sub.endsAt ? formatDate(sub.endsAt) : t('sub.noEnd'));
}

function subscriptionSection(): HTMLElement {
  const ent = state.session?.entitlements;
  if (!ent) return el('div', {});
  const sub = state.session?.subscription;
  const paid = ent.planCode !== 'free';
  const rows = el('div', { class: 'rows' });

  // Başarısız yenileme en üstte: eskiden müşteri hiçbir şey görmüyor, paketi
  // sessizce düşüyordu.
  if (sub?.paymentFailed) {
    rows.appendChild(el('div', { class: 'row sub-alert' },
      el('div', { class: 'grow' },
        el('b', {}, iconEl('info'), t('sub.unpaidTitle')),
        el('div', { class: 'meta' }, ent.expired || !sub.endsAt
          ? t('sub.unpaidEndedBody')
          : t('sub.unpaidBody', { date: formatDate(sub.endsAt) })))));
  }

  const planRow = infoRow(t('sub.active'), ent.planName + (ent.expired ? t('sub.expiredSuffix') : ''));
  if (sub?.inTrial && !ent.expired) {
    planRow.appendChild(el('span', { class: 'tag-soft' }, t('sub.trialTag')));
  }
  rows.appendChild(planRow);
  if (paid && !ent.expired) {
    if (sub?.period) rows.appendChild(infoRow(t('sub.period'), sub.period === 'yearly' ? t('bill.yearly') : t('bill.monthly')));
    rows.appendChild(subscriptionDateRow(ent, sub));
  }
  if (sub?.pendingPlan && sub.nextChargeAt) {
    rows.appendChild(infoRow(t('sub.pendingPlan'), t('sub.pendingPlanValue', { plan: sub.pendingPlan.name, date: formatDate(sub.nextChargeAt) })));
  }
  // Kart bu sitede tutulmaz ve ödeme sağlayıcısı kart bilgisini vermez: gösterilemez,
  // ama sağlayıcının formundan değiştirilebilir.
  if (sub?.canUpdateCard) {
    rows.appendChild(buttonRow(t('sub.cardTitle'), t('sub.cardBody'), t('sub.cardUpdate'), 'btn btn-outline btn-sm', () => void updateCard()));
  }
  rows.appendChild(infoRow(t('sub.concurrent'), t('sub.concurrentValue', { n: ent.concurrentStreams })));
  rows.appendChild(infoRow(t('sub.iptv'), ent.iptv ? t('sub.on') : t('sub.off')));

  if (paid) {
    // Sağlayıcıda tahsilatı süren (ya da ödenmemiş) abonelik: süresi dolmuş olsa
    // da iptal edilebilmeli; aksi hâlde müşteri yeniden satın alamaz.
    const billed = sub?.status === 'active' || sub?.status === 'unpaid';
    let action: HTMLElement | null;
    if (ent.expired && !billed) {
      // Süresi dolmuş pakette "dönem sonunda sona erecek" yazmaya devam ediyordu.
      action = el('div', { class: 'row' },
        el('div', { class: 'grow' }, el('b', {}, t('sub.endedTitle')), el('div', { class: 'meta' }, t('sub.endedBody'))));
    } else if (ent.cancelAtPeriodEnd && ent.canResume === false) {
      // Sağlayıcıda iptal edilen abonelik geri açılamaz: düğme yerine ne olacağı yazar.
      action = el('div', { class: 'row' },
        el('div', { class: 'grow' }, el('b', {}, t('sub.endingTitle')), el('div', { class: 'meta' }, t('sub.endingNoResume'))));
    } else if (ent.cancelAtPeriodEnd) {
      action = buttonRow(t('sub.endingTitle'), t('sub.endingBody'), t('sub.resume'), 'btn btn-outline btn-sm', () => void resumeSub());
    } else {
      action = buttonRow(t('sub.title'), t('sub.cancelBody'), t('sub.cancel'), 'btn btn-danger btn-sm', () => void cancelSub());
    }
    rows.appendChild(action);
  }

  return el('section', { style: { marginTop: '8px' } },
    el('h2', { style: { fontSize: '18px', fontWeight: '600', marginBottom: '12px' } }, t('sub.section')),
    rows,
  );
}

/** İşlem geçmişi satırının sağındaki tutar: deneme başlangıcında ve tahsilat
 *  olmayan işlemlerde "0,00 TL" yerine ne olduğunu söyler. */
function historyAmount(entry: BillingHistoryEntry): string {
  if (entry.kind === 'trial_started') return t('sub.noCharge');
  if (entry.kind === 'purchase' || entry.kind === 'renewal' || entry.kind === 'payment_failed') return entry.amount_label;
  return '';
}

export function historyRow(entry: BillingHistoryEntry): HTMLElement {
  const title = t(`sub.ev.${entry.kind}`, { plan: entry.plan_name || '—' });
  const meta = [
    formatDate(entry.created_at),
    entry.period ? (entry.period === 'yearly' ? t('bill.yearly') : t('bill.monthly')) : '',
    t('sub.historyRef', { ref: entry.ref }),
  ].filter(Boolean).join(' · ');
  const amount = historyAmount(entry);
  return el('div', { class: 'row history-row' + (entry.kind === 'payment_failed' ? ' is-failed' : '') },
    el('div', { class: 'grow' }, el('b', {}, title), el('div', { class: 'meta' }, meta)),
    entry.test ? el('span', { class: 'tag-soft tag-test' }, 'TEST') : null,
    amount ? el('span', { class: 'history-amount' }, amount) : null,
  );
}

/** İşlem geçmişi: sunucu bu kayıtları baştan beri tutuyordu ama hiçbir ekranda
 *  gösterilmiyordu. Bölüm önce iskeletle çizilir, kayıtlar sonradan dolar. */
function historySection(stale: () => boolean): HTMLElement {
  const body = el('div', { class: 'rows' }, skeletonRows(2));
  void (async () => {
    let content: HTMLElement;
    try {
      const { events } = await api.get<{ events: BillingHistoryEntry[] }>('/api/billing/history');
      content = events.length
        ? el('div', { class: 'rows' }, ...events.map(historyRow))
        : el('p', { class: 'inline-note', style: { display: 'block' } }, t('sub.historyEmpty'));
    } catch {
      content = el('p', { class: 'inline-note', style: { display: 'block' } }, t('sub.historyFail'));
    }
    if (!stale()) body.replaceWith(content);
  })();
  return el('section', { style: { marginTop: '36px' } },
    el('h2', { style: { fontSize: '18px', fontWeight: '600', marginBottom: '6px' } }, t('sub.history')),
    el('p', { class: 'inline-note', style: { marginBottom: '12px', display: 'block', maxWidth: '62ch' } }, t('sub.historyLead')),
    body,
  );
}

function addonSection(data: PlansPayload): HTMLElement {
  // Satışa açık olmayan ekler gösterilmez; boş bölüm de çizilmez.
  const addons = data.addons.filter((addon) => addon.sellable);
  if (!addons.length) return el('div', {});
  return el('section', { style: { marginTop: '36px' } },
    el('h2', { style: { fontSize: '18px', fontWeight: '600', marginBottom: '6px' } }, t('addons.title')),
    el('p', { class: 'inline-note', style: { marginBottom: '12px', display: 'block', maxWidth: '62ch' } }, t('addons.body')),
    el('div', { class: 'rows' }, ...addons.map((addon) => el('div', { class: 'row' },
      el('div', { class: 'grow' },
        el('b', {}, addon.name),
        el('div', { class: 'meta' }, `${addon.description} · ${t('addons.requires', { plan: planLabel(addon.requiresPlan) })}`),
      ),
      el('span', { class: 'tag-soft' }, addon.priceLabel),
    ))),
  );
}

function planLabel(code: string): string {
  if (code === 'free') return t('plans.name.free');
  if (code === 'starter') return t('plans.name.starter');
  if (code === 'plus') return t('plans.name.plus');
  return code;
}

function legalLink(section: string, label: string): HTMLElement {
  const button = el('button', { class: 'btn btn-outline btn-sm', type: 'button' }, label);
  onTap(button, () => navigate(`/legal?section=${section}`));
  return button;
}

function infoRow(label: string, value: string): HTMLElement {
  return el('div', { class: 'row' },
    el('div', { class: 'grow' }, el('b', {}, label), el('div', { class: 'meta' }, value)),
  );
}

function buttonRow(title: string, meta: string, label: string, klass: string, action: () => void): HTMLElement {
  const button = el('button', { class: klass, type: 'button' }, label);
  onTap(button, action);
  return el('div', { class: 'row' },
    el('div', { class: 'grow' }, el('b', {}, title), el('div', { class: 'meta' }, meta)),
    button,
  );
}

async function cancelSub(): Promise<void> {
  // Ödeme dönemi dolmuş (yenilemesi alınamamış) abonelikte tolerans iptalle kalkar:
  // erişim dönem sonunda değil, hemen biter. Pencere ve bildirim bunu söyler.
  const before = state.session;
  const endsNow = !!before?.entitlements.expired
    || (before?.subscription?.status === 'unpaid' && !!before.entitlements.renewsAt && before.entitlements.renewsAt <= Date.now());
  const ok = await confirmDialog({
    title: t('sub.cancelDialogTitle'),
    body: t(endsNow ? 'sub.cancelDialogBodyNow' : 'sub.cancelDialogBody'),
    confirmLabel: t('sub.cancelConfirm'), cancelLabel: t('sub.keep'), danger: true,
  });
  if (!ok) return;
  try {
    await api.post('/api/billing/cancel');
    await loadSession();
    toast(t(endsNow ? 'sub.cancelledNow' : 'sub.cancelled'), 'ok');
    void plansView();
  } catch (err) { toast((err as Error).message, 'error'); }
}

async function updateCard(): Promise<void> {
  try {
    const result = await api.post<{ redirectUrl: string }>('/api/billing/card-update');
    // Kart formu sağlayıcının betiğiyle sunucunun ayrı sayfasında açılır (tam sayfa geçişi).
    location.assign(result.redirectUrl);
  } catch (err) { toast((err as Error).message, 'error'); }
}

async function resumeSub(): Promise<void> {
  try {
    await api.post('/api/billing/resume');
    await loadSession();
    toast(t('sub.resumed'), 'ok');
    void plansView();
  } catch (err) { toast((err as Error).message, 'error'); }
}

// ============================== Test ödemesi ================================

export async function mockPaymentView(_params: Record<string, string>, query: URLSearchParams): Promise<void> {
  ensureAppShell();
  const host = appMain();
  const checkoutId = query.get('checkout') ?? '';
  const planName = query.get('plan') === 'plus' ? t('plans.name.plus') : t('plans.name.starter');

  const status = el('div', {});
  // "Ödemeyi tamamla" gerçek bir tahsilat izlenimi veriyordu: TEST rozetini
  // okumayan kullanıcı abonelik satın aldığını sanıyordu. Düğme ne yaptığını söyler.
  const payBtn = el('button', { class: 'btn btn-light', type: 'button' }, t('pay.activate'));
  const cancelBtn = el('button', { class: 'btn btn-quiet', type: 'button' }, t('common.cancel'));
  onTap(cancelBtn, () => navigate('/plans'));

  onTap(payBtn, async () => {
    payBtn.disabled = true;
    payBtn.textContent = t('pay.processing');
    try {
      const result = await api.post<{ notice: string }>('/api/billing/mock/confirm', { checkoutId });
      await loadSession();
      toast(result.notice, 'ok', 6000);
      navigate('/plans');
    } catch (err) {
      mount(status, errorNote((err as Error).message));
      payBtn.disabled = false;
      payBtn.textContent = t('pay.activate');
    }
  });

  mount(host, wrap(
    pageIntro(t('pay.title')),
    el('div', { class: 'panel', style: { maxWidth: '560px' } },
      el('h2', {}, t('pay.plan', { name: planName })),
      el('p', {},
        el('span', { class: 'tag-soft tag-test' }, 'TEST'),
        t('pay.testNote')),
      el('div', { class: 'rows' },
        infoRow(t('pay.package'), planName),
        infoRow(t('pay.period'), new URLSearchParams(location.search).get('period') === 'yearly'
          ? t('pay.periodValueYearly') : t('pay.periodValue')),
        infoRow(t('pay.charge'), t('pay.chargeValue')),
        infoRow(t('pay.record'), t('pay.recordValue')),
      ),
      status,
      el('div', { class: 'modal-actions' }, cancelBtn, payBtn),
    ),
  ));
}

// ================================= Ayarlar ==================================

/** Parola değiştir: giriş yapmış kullanıcı içindir. Parolasını unutan
 *  kullanıcı giriş ekranından e-postayla sıfırlar (views/auth.ts). */
async function changePassword(): Promise<void> {
  const current = el('input', { type: 'password', autocomplete: 'current-password', placeholder: t('settings.password.current') });
  const next = el('input', { type: 'password', autocomplete: 'new-password', placeholder: t('settings.password.newPlaceholder') });
  const body = el('div', { style: { display: 'grid', gap: '12px' } },
    el('p', {}, t('settings.password.note')),
    el('div', { class: 'field' }, el('label', {}, t('settings.password.current')), current),
    el('div', { class: 'field' }, el('label', {}, t('settings.password.new')), next),
  );
  const ok = await confirmDialog({ title: t('settings.password.title'), body, confirmLabel: t('settings.password.confirm') });
  if (!ok) return;
  try {
    const result = await api.post<{ notice: string }>('/api/auth/password', {
      currentPassword: current.value, newPassword: next.value,
    });
    toast(result.notice, 'ok', 6000);
  } catch (err) {
    toast(err instanceof ApiError ? err.message : t('settings.password.fail'), 'error', 6000);
  }
}

/**
 * Hesabı sil. Gizlilik metni bu hakkı veriyor; ürün içinde de bir karşılığı
 * olmalı. Parola tekrar sorulur ve yedeklerdeki kopya saklanmaz.
 */
async function deleteAccount(): Promise<void> {
  const pass = el('input', { type: 'password', autocomplete: 'current-password', placeholder: t('settings.delete.placeholder') });
  const body = el('div', { style: { display: 'grid', gap: '12px' } },
    el('p', {}, t('settings.delete.body1')),
    el('p', {}, t('settings.delete.body2')),
    el('div', { class: 'field' }, el('label', {}, t('settings.delete.label')), pass),
  );
  const ok = await confirmDialog({
    title: t('settings.delete.title'),
    body,
    confirmLabel: t('settings.delete.confirm'), danger: true,
  });
  if (!ok) return;
  try {
    const result = await api.del<{ notice: string }>('/api/auth/me', { password: pass.value });
    alertThenLeave(result.notice);
  } catch (err) {
    toast(err instanceof ApiError ? err.message : t('settings.delete.fail'), 'error', 7000);
  }
}

function alertThenLeave(notice: string): void {
  toast(notice, 'ok', 8000);
  setTimeout(() => location.assign('/'), 1500);
}

export async function settingsView(_params?: Record<string, string>, query?: URLSearchParams): Promise<void> {
  ensureAppShell();
  const host = appMain();
  const ent = state.session?.entitlements;
  const formOpen = query?.get('form') === 'open';

  // Çıkış sayfanın EN ALTINDA ve ikincil: sağ üstte duran en dikkat çekici
  // düğme yıkıcı bir eylemdi ve kaynak eklemeye giden yolun üzerindeydi.
  const logoutBtn = el('button', { class: 'btn btn-quiet btn-sm', type: 'button' }, iconEl('logout'), t('settings.logout'));
  onTap(logoutBtn, () => void logout());

  const deleteBtn = el('button', { class: 'btn btn-danger btn-sm', type: 'button' }, iconEl('trash'), t('settings.deleteBtn'));
  onTap(deleteBtn, () => void deleteAccount());

  const passBtn = el('button', { class: 'btn btn-outline btn-sm', type: 'button' }, iconEl('lock'), t('settings.passBtn'));
  onTap(passBtn, () => void changePassword());

  mount(host, wrap(
    pageIntro(t('settings.title'), null, state.session?.user.email ?? ''),
    el('div', { class: 'settings' },
      el('section', { id: 'sources' }, el('h2', {}, t('sources.title')), skeletonRows(2)),
      el('section', { id: 'devices' }, el('h2', {}, t('devices.title')), skeletonRows(1)),
      el('section', {},
        el('h2', {}, t('settings.subscription')),
        el('p', { class: 'lead' }, t('settings.subLead')),
        el('div', { class: 'rows' },
          infoRow(t('settings.package'), ent?.planName ?? '—'),
          ...(ent && ent.planCode !== 'free' && !ent.expired ? [subscriptionDateRow(ent, state.session?.subscription)] : []),
          infoRow(t('settings.dailyTime'), ent?.dailySeconds
            ? t('settings.dailyValue', { n: Math.round(ent.dailySeconds / 60), tz: state.session?.quota.timezone ?? '' })
            : t('settings.dailyNone')),
          buttonRow(t('settings.plansRow'), t('settings.plansRowMeta'), t('settings.plansRowBtn'), 'btn btn-outline btn-sm',
            () => navigate('/plans')),
        ),
      ),
      el('section', {},
        el('h2', {}, t('lang.label')),
        el('p', { class: 'lead' }, t('lang.lead')),
        langSwitch('block'),
      ),
      el('section', { id: 'support' }),
      el('section', {},
        el('h2', {}, t('meta.legal')),
        el('p', { class: 'lead' }, t('settings.legalLead')),
        el('div', { class: 'row-actions' }, legalLink('terms', t('auth.consent.terms')), legalLink('privacy', t('legal.privacy')),
          legalLink('subscription', t('legal.subscription')), legalLink('contact', t('legal.contact'))),
      ),
      el('section', { class: 'settings-end' },
        el('h2', {}, t('settings.security')),
        el('p', { class: 'lead' }, t('settings.securityLead')),
        el('div', { class: 'row-actions' }, passBtn, logoutBtn),
      ),
      el('section', { class: 'settings-end' },
        el('h2', {}, t('settings.deleteSection')),
        el('p', { class: 'lead' }, t('settings.deleteLead')),
        deleteBtn,
      ),
    ),
  ));

  void renderSources(host.querySelector('#sources') as HTMLElement, formOpen);
  void renderDevices(host.querySelector('#devices') as HTMLElement);
  void renderSupport(host.querySelector('#support') as HTMLElement);
}

interface SourceView {
  id: string; name: string; type: string; host: string | null; username: string | null;
  hasEpg: boolean; lastSyncAt: number | null; lastSyncStatus: string | null; lastSyncError: string | null;
  counts: Record<string, number>;
}

function countWord(key: string): string {
  if (key === 'live') return t('sources.count.live');
  if (key === 'movie') return t('sources.count.movie');
  if (key === 'series') return t('sources.count.series');
  if (key === 'episode') return t('sources.count.episode');
  return key;
}

async function renderSources(host: HTMLElement, formOpen = false): Promise<void> {
  let data: { sources: SourceView[]; locked: boolean };
  try {
    data = await api.get('/api/sources');
  } catch (err) {
    mount(host, el('h2', {}, t('sources.title')), stateBlock({ title: t('sources.loadFail'), body: (err as Error).message }));
    return;
  }

  if (data.locked) {
    mount(host,
      el('h2', {}, t('sources.title')),
      el('p', { class: 'lead' }, t('sources.lockedLead')),
      stateBlock({
        title: t('sources.lockedTitle'),
        body: t('sources.lockedBody'),
        action: { label: t('common.viewPlans'), onClick: () => navigate('/plans') },
      }),
    );
    return;
  }

  const slots = state.session?.entitlements.sourceSlots ?? 0;
  const list = el('div', { class: 'rows' });

  for (const source of data.sources) {
    const counts = Object.entries(source.counts).filter(([, n]) => n > 0)
      .map(([key, n]) => `${n} ${countWord(key)}`)
      .join(' · ');

    const syncBtn = el('button', { class: 'btn btn-outline btn-sm', type: 'button' }, iconEl('refresh'), t('sources.sync'));
    onTap(syncBtn, async () => {
      syncBtn.disabled = true;
      try {
        const result = await api.post<{ warnings: string[] }>(`/api/sources/${source.id}/sync`);
        toast(result.warnings.length ? result.warnings.join(' ') : t('sources.synced'),
          result.warnings.length ? 'info' : 'ok', 6000);
      } catch (err) {
        // Yenileme başarısızsa çalışan eski katalog SİLİNMEZ.
        toast(t('sources.syncFail', { error: (err as Error).message }), 'error', 7000);
      }
      void renderSources(host);
    });

    const deleteBtn = el('button', { class: 'btn btn-danger btn-sm', type: 'button' }, iconEl('trash'), t('sources.delete'));
    onTap(deleteBtn, async () => {
      const ok = await confirmDialog({
        title: t('sources.deleteTitle'),
        body: t('sources.deleteBody', { name: source.name }),
        confirmLabel: t('sources.delete'), danger: true,
      });
      if (!ok) return;
      try { await api.del(`/api/sources/${source.id}`); toast(t('sources.deleted'), 'ok'); }
      catch (err) { toast((err as Error).message, 'error'); }
      void renderSources(host);
    });

    list.appendChild(el('div', { class: 'row' },
      el('div', { class: 'grow' },
        el('b', {}, source.name, ' ', el('span', { class: 'tag-soft' }, source.type === 'xtream' ? 'Xtream' : 'M3U')),
        el('div', { class: 'meta' }, `${source.host ?? ''}${source.username ? ' · ' + source.username : ''}`),
        el('div', { class: 'meta' }, source.lastSyncStatus === 'ok'
          ? `${t('sources.updated', { time: relativeTime(source.lastSyncAt) })}${counts ? ' · ' + counts : ''}`
          : source.lastSyncError
            ? t('sources.lastFail', { error: source.lastSyncError })
            : t('sources.notSynced')),
      ),
      el('div', { class: 'actions' }, syncBtn, deleteBtn),
    ));
  }

  const canAdd = data.sources.length < slots;

  // Kaydedilen kaynaktan sonra "sırada ne var" belirsizdi: kanallara giden
  // düğmeyi doğrudan veriyoruz.
  const goChannels = el('button', { class: 'btn btn-light btn-sm', type: 'button' }, iconEl('antenna'), t('sources.goChannels'));
  onTap(goChannels, () => navigate('/iptv'));

  let adder: HTMLElement;
  if (!canAdd) {
    adder = el('p', { class: 'inline-note', style: { marginTop: '16px' } }, t('sources.slotsFull'));
  } else if (isCar() && !formOpen) {
    // Araç ekranında uzun adres/kullanıcı/parola yazmak fiilen imkânsız.
    const toPhone = el('button', { class: 'btn btn-light', type: 'button' }, iconEl('phone'), t('sources.addOnPhone'));
    onTap(toPhone, () => navigate('/handoff?task=source'));
    const anyway = el('button', { class: 'btn btn-quiet btn-sm', type: 'button' }, t('sources.anyway'));
    onTap(anyway, () => void renderSources(host, true));
    adder = el('div', { class: 'panel', style: { marginTop: '20px' } },
      el('h2', {}, t('sources.phoneTitle')),
      el('p', {}, t('sources.phoneBody')),
      el('div', { class: 'modal-actions' }, anyway, toPhone),
    );
  } else if (data.sources.length && !formOpen) {
    // Kayıtlı kaynak varken ikinci form açık duruyordu: sayfa uzuyor ve
    // "eklediğim kaydedildi mi?" tereddüdü doğuruyordu.
    const open = el('button', { class: 'btn btn-outline btn-sm', type: 'button' }, iconEl('plus'), t('sources.addNew'));
    onTap(open, () => void renderSources(host, true));
    adder = el('div', { class: 'row-actions', style: { marginTop: '18px' } }, open, goChannels);
  } else {
    adder = sourceForm(() => void renderSources(host));
  }

  mount(host,
    el('h2', {}, t('sources.title')),
    el('p', { class: 'lead' }, t('sources.lead', { n: slots })),
    data.sources.length ? list : stateBlock({
      title: t('sources.emptyTitle'),
      body: t('sources.emptyBody'),
    }),
    adder,
  );
}

/** 480–560 px panel; alanla ilişkili hata, maskeli parola, form verisi korunur. */
function sourceForm(onDone: () => void): HTMLElement {
  const typeSelect = el('select', {},
    el('option', { value: 'xtream' }, t('sources.form.xtream')),
    el('option', { value: 'm3u' }, t('sources.form.m3u')),
  );
  const nameInput = el('input', { type: 'text', placeholder: t('sources.form.namePlaceholder'), maxlength: '60' });
  const urlInput = el('input', { type: 'url', placeholder: 'https://sunucu.example/liste.m3u', autocomplete: 'off' });
  const epgInput = el('input', { type: 'url', placeholder: 'https://sunucu.example/epg.xml', autocomplete: 'off' });
  const hostInput = el('input', { type: 'text', placeholder: 'http://sunucu.example:8080', autocomplete: 'off' });
  const userInput = el('input', { type: 'text', placeholder: t('sources.form.userPlaceholder'), autocomplete: 'off' });
  const passInput = el('input', { type: 'password', placeholder: '••••••••', autocomplete: 'new-password' });

  const m3uFields = el('div', { hidden: true },
    field(t('sources.form.m3uLabel'), urlInput, t('sources.form.m3uHint')),
    field(t('sources.form.epgLabel'), epgInput, t('sources.form.epgHint')),
  );
  const xtreamFields = el('div', {},
    field(t('sources.form.hostLabel'), hostInput, t('sources.form.hostHint')),
    field(t('sources.form.userLabel'), userInput),
    field(t('sources.form.passLabel'), passInput, t('sources.form.passHint')),
  );

  typeSelect.addEventListener('change', () => {
    const isM3u = typeSelect.value === 'm3u';
    m3uFields.hidden = !isM3u;
    xtreamFields.hidden = isM3u;
  });

  const status = el('div', {});
  const testBtn = el('button', { class: 'btn btn-outline btn-sm', type: 'button' }, t('sources.form.test'));
  const saveBtn = el('button', { class: 'btn btn-light btn-sm', type: 'button' }, t('sources.form.save'));

  const payload = () => ({
    type: typeSelect.value,
    name: nameInput.value,
    url: urlInput.value,
    epgUrl: epgInput.value,
    host: hostInput.value,
    username: userInput.value,
    password: passInput.value,
  });

  onTap(testBtn, async () => {
    mount(status, el('p', { class: 'inline-note' }, t('sources.form.testing')));
    try {
      const result = await api.post<{ detail: string }>('/api/sources/test', payload());
      mount(status, okNote(result.detail));
    } catch (err) {
      mount(status, errorNote((err as Error).message));
    }
  });

  onTap(saveBtn, async () => {
    saveBtn.disabled = true;
    mount(status, el('p', { class: 'inline-note' }, t('sources.form.saving')));
    try {
      const result = await api.post<{ warnings: string[] }>('/api/sources', payload());
      toast(t('sources.form.added') + (result.warnings.length ? ' ' + result.warnings.join(' ') : ''),
        result.warnings.length ? 'info' : 'ok', 7000);
      mount(status, '');
      onDone();
    } catch (err) {
      // Form verisi korunur; hata alanla birlikte gösterilir.
      mount(status, errorNote((err as Error).message));
    } finally {
      saveBtn.disabled = false;
    }
  });

  return el('div', { class: 'panel', style: { marginTop: '20px', maxWidth: '560px' } },
    el('h2', {}, t('sources.form.title')),
    el('p', {}, t('sources.form.lead')),
    field(t('sources.form.typeLabel'), typeSelect),
    field(t('sources.form.nameLabel'), nameInput),
    xtreamFields, m3uFields,
    status,
    el('div', { class: 'modal-actions' }, testBtn, saveBtn),
  );
}

function field(label: string, input: HTMLElement, hint?: string): HTMLElement {
  const id = 'f_' + Math.random().toString(36).slice(2, 9);
  input.id = id;
  return el('div', { class: 'field' },
    el('label', { for: id }, label),
    input,
    hint ? el('div', { class: 'hint' }, hint) : null,
  );
}

async function renderDevices(host: HTMLElement): Promise<void> {
  try {
    const data = await api.get<{
      devices: Array<{ id: string; name: string; kind: string; last_seen_at: number; active_sessions: number }>;
      currentDeviceId: string | null;
    }>('/api/auth/devices');

    const list = el('div', { class: 'rows' }, ...data.devices.map((device) => {
      // Kırmızı yalnız BAŞKA cihazı çıkarmak için; bu cihaz için sessiz düğme.
      const isCurrent = device.id === data.currentDeviceId;
      const removeBtn = el('button', {
        class: isCurrent ? 'btn btn-quiet btn-sm' : 'btn btn-outline btn-sm', type: 'button',
      }, isCurrent ? t('devices.signOutHere') : t('devices.remove'));
      onTap(removeBtn, async () => {
        try {
          await api.del(`/api/auth/devices/${device.id}`);
          if (isCurrent) { location.assign('/login'); return; }
          toast(t('devices.removed'), 'ok');
          void renderDevices(host);
        } catch (err) { toast((err as Error).message, 'error'); }
      });
      return el('div', { class: 'row' },
        el('div', { class: 'grow' },
          el('b', {}, device.name,
            isCurrent ? el('span', { class: 'tag-soft', style: { marginLeft: '8px' } }, t('devices.thisDevice')) : null),
          el('div', { class: 'meta' },
            t('devices.lastSeen', { time: relativeTime(device.last_seen_at), n: device.active_sessions })),
        ),
        removeBtn,
      );
    }));

    mount(host,
      el('h2', {}, t('devices.title')),
      el('p', { class: 'lead' }, t('devices.lead')),
      data.devices.length ? list : stateBlock({ title: t('devices.none') }),
    );
  } catch {
    mount(host, '');
  }
}

/** Destek görünümü: gerçek değerler, teknik istatistikler burada. */
async function renderSupport(host: HTMLElement): Promise<void> {
  let health: { testMedia: boolean; mockPayments: boolean; env: string } | null = null;
  try { health = await api.get('/api/health'); } catch { /* uç kapalıysa bölüm yine çıkar */ }

  const rows = el('div', { class: 'rows' },
    infoRow(t('support.build'), state.buildId),
    infoRow(t('support.webcodecs'), 'VideoDecoder' in window ? t('support.supported') : t('support.unsupported')),
    infoRow(t('support.deviceLabel'), api.deviceLabel()),
    infoRow(t('support.browser'), navigator.userAgent.slice(0, 110)),
  );

  // Tanı klibi yalnız geliştirme/test ortamında görünür.
  if (health?.testMedia) {
    const clipBtn = el('button', { class: 'btn btn-outline btn-sm', type: 'button' }, iconEl('play'), t('support.clip'));
    onTap(clipBtn, () => void play({ source: { type: 'test' }, title: t('support.clipTitle') }));
    rows.appendChild(el('div', { class: 'row' },
      el('div', { class: 'grow' },
        el('b', {}, t('support.clipRow'), el('span', { class: 'tag-soft tag-test' }, health.env)),
        el('div', { class: 'meta' }, t('support.clipMeta')),
      ),
      clipBtn,
    ));
  }

  mount(host,
    el('h2', {}, t('support.title')),
    el('p', { class: 'lead' }, t('support.lead')),
    rows,
  );
}
